import { externalLinkProps } from "@/utils/externalLinkProps";
import classNames from "classnames";
import Link from "next/link";
import { ButtonHTMLAttributes, ReactNode } from "react";

export type IconButtonSize = "sm" | "md";
export type IconButtonShape = "circle" | "square";

interface BaseProps {
	icon: ReactNode;
	label: string;
	size?: IconButtonSize;
	shape?: IconButtonShape;
	className?: string;
}

interface LinkProps extends BaseProps {
	href: string;
}

type ButtonProps = BaseProps & { href?: undefined } & Pick<
		ButtonHTMLAttributes<HTMLButtonElement>,
		"onClick" | "disabled" | "aria-expanded" | "aria-controls"
	>;

const SIZES: Record<IconButtonSize, string> = {
	sm: "size-10",
	md: "size-11",
};

const SHAPES: Record<IconButtonShape, string> = {
	circle: "rounded-full",
	square: "rounded-control",
};

export default function IconButton(props: LinkProps | ButtonProps) {
	const { icon, label, size = "md", shape = "circle", className } = props;

	if (props.href !== undefined) {
		return (
			<Link
				href={props.href}
				{...externalLinkProps(props.href)}
				aria-label={label}
				className={classNames(
					"flex shrink-0 items-center justify-center border border-front/30 hover:bg-front/10 outline-primary focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-30 transition-all cursor-pointer",
					SIZES[size],
					SHAPES[shape],
					className
				)}
			>
				{icon}
			</Link>
		);
	}

	return (
		<button
			type="button"
			aria-label={label}
			aria-expanded={props["aria-expanded"]}
			aria-controls={props["aria-controls"]}
			disabled={props.disabled}
			onClick={props.onClick}
			className={classNames(
				"flex shrink-0 items-center justify-center border border-front/30 hover:bg-front/10 outline-primary focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-30 transition-all cursor-pointer",
				SIZES[size],
				SHAPES[shape],
				className
			)}
		>
			{icon}
		</button>
	);
}
